import { ManualPracticeEditor, saveManualPracticeEntry } from "../ManualPracticeEditor.js";
import { PitchPracticeLink } from "../pitch/PitchPracticeLink.js";
import { ActionConfirmation } from "../ui/ActionConfirmation.js";
import { MIN_TIMER_SECONDS } from "@wmf/shared/practice";
import { useOptionalAuth } from "../auth.js";
import { PracticeInstrumentPicker, usePracticeInstrument } from "../practiceInstrument.js";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { get, send, type PracticeLog, type PracticeSession } from "../api.js";
import {
  dayKey,
  formatDate,
  formatDuration,
  formatWeekdayDay,
} from "../format.js";
import { PracticeAttachments } from "../PracticeAttachments.js";
import {
  PracticeHistory,
  type PracticeHistoryDay,
} from "../PracticeHistory.js";
import { useAsync } from "../useAsync.js";
import { StudentNeckDiagrams } from "../StudentNeckDiagrams.js";
import { FamilyPracticeTodos } from "./FamilyPracticeTodos.js";
import { FamilyResources } from "./FamilyResources.js";
import "../familyPracticeWorkspace.css";

/**
 * One overall practice timer with optional task-level focus detail.
 *
 * Switching between assigned work and Free practice never stops the clock. The
 * server keeps those timings under one overall practice-session row, so every
 * existing summary still means "one time I practised", not "one task I touched".
 */

const COMMON_MINUTES = [10, 15, 20, 30, 45];

interface FocusPracticeTimer extends NonNullable<PracticeLog["timer"]> {
  practiceTodoId: string | null;
  focusName: string | null;
  focusStartedAt: string | null;
  suggestedMinutes: number | null;
}

interface PracticeFocusSegment {
  id: string;
  practiceTodoId: string | null;
  focusName: string;
  startedAt: string;
  endedAt: string;
  durationSeconds: number;
  notes: string | null;
  needsWork: boolean;
}

interface FocusPracticeSession extends PracticeSession {
  focusSegments: PracticeFocusSegment[];
}

type RawFocusPracticeTimer = NonNullable<PracticeLog["timer"]> &
  Partial<
    Pick<
      FocusPracticeTimer,
      "practiceTodoId" | "focusName" | "focusStartedAt" | "suggestedMinutes"
    >
  >;

type RawFocusPracticeSession = PracticeSession & { focusSegments?: unknown };

type RawFocusPracticeLog = Omit<PracticeLog, "timer" | "sessions"> & {
  timer: RawFocusPracticeTimer | null;
  sessions: RawFocusPracticeSession[];
};

type FocusPracticeLog = Omit<PracticeLog, "timer" | "sessions"> & {
  timer: FocusPracticeTimer | null;
  sessions: FocusPracticeSession[];
};

function isFocusSegment(value: unknown): value is PracticeFocusSegment {
  if (value === null || typeof value !== "object") return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row["id"] === "string" &&
    (row["practiceTodoId"] === null ||
      typeof row["practiceTodoId"] === "string") &&
    typeof row["focusName"] === "string" &&
    typeof row["startedAt"] === "string" &&
    typeof row["endedAt"] === "string" &&
    typeof row["durationSeconds"] === "number" &&
    Number.isFinite(row["durationSeconds"]) &&
    (row["notes"] === null || typeof row["notes"] === "string") &&
    typeof row["needsWork"] === "boolean"
  );
}

function normaliseTimer(
  timer: RawFocusPracticeTimer | null,
): FocusPracticeTimer | null {
  if (timer === null) return null;
  return {
    ...timer,
    practiceTodoId:
      typeof timer.practiceTodoId === "string" ? timer.practiceTodoId : null,
    focusName: typeof timer.focusName === "string" ? timer.focusName : null,
    focusStartedAt:
      typeof timer.focusStartedAt === "string" ? timer.focusStartedAt : null,
    suggestedMinutes:
      typeof timer.suggestedMinutes === "number" &&
      Number.isFinite(timer.suggestedMinutes)
        ? timer.suggestedMinutes
        : null,
  };
}

function normaliseFocusSegments(value: unknown): PracticeFocusSegment[] {
  return Array.isArray(value) ? value.filter(isFocusSegment) : [];
}

function normalisePracticeLog(log: RawFocusPracticeLog): FocusPracticeLog {
  return {
    ...log,
    timer: normaliseTimer(log.timer),
    sessions: log.sessions.map((session) => ({
      ...session,
      focusSegments: normaliseFocusSegments(session.focusSegments),
    })),
  };
}

/** Elapsed time as a clock, which is what a running timer should look like. */
function clock(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const parts = [
    Math.floor(whole / 3600),
    Math.floor((whole % 3600) / 60),
    whole % 60,
  ];
  if (parts[0] === 0) parts.shift();
  return parts
    .map((part, index) =>
      index === 0 ? String(part) : String(part).padStart(2, "0"),
    )
    .join(":");
}

export function Practice({ studentId }: { studentId: string }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const instrument=usePracticeInstrument(studentId);
  const toolLinks=useOptionalAuth()?.me?.practiceToolLinksEnabled===true;
  const base = `/me/students/${studentId}/practice`;
  const { data, error, reload } = useAsync(async () => {
    return { ...normalisePracticeLog(await get<RawFocusPracticeLog>(base)), receivedAt: Date.now() };
  }, [base]);

  // Manual logging stays available for practice that was not timed.
  const [minutes, setMinutes] = useState("");
  const [seconds, setSeconds] = useState("");
  const [name, setName] = useState("");
  const [manualNeedsWork, setManualNeedsWork] = useState(false);

  // Reflection belongs to the focus being left, either by switching or by
  // finishing the overall practice. It is deliberately optional.
  const [focusNotes, setFocusNotes] = useState("");
  const [focusNeedsWork, setFocusNeedsWork] = useState(false);
  const [freePracticeName, setFreePracticeName] = useState("");
  const [freePracticeOpen, setFreePracticeOpen] = useState(false);
  const manualLogRef = useRef<HTMLDetailsElement>(null);
  const handledLogTitle = useRef<string | null>(null);
  const [focusRevision, setFocusRevision] = useState(0);

  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<PracticeHistoryDay | null>(
    null,
  );
  const [historySessions, setHistorySessions] = useState<FocusPracticeSession[]>([]);
  const [historyCursor, setHistoryCursor] = useState<string | null>(null);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  /** Only manual/legacy sessions can be corrected as one undivided entry. */
  const [editing, setEditing] = useState<{
    id: string;
    minutes: string;
    seconds: string;
    name: string;
  } | null>(null);

  const asMinutes = (m: string, sec: string) =>
    Number(m || 0) + Number(sec || 0) / 60;

  /** A second hand for the timer, only while one is running. */
  const [tick, setTick] = useState(() => Date.now());
  const running = data?.timer != null;
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running]);

  useEffect(() => {
    if (!data) {
      setHistorySessions([]);
      setHistoryCursor(null);
      setHistoryHasMore(false);
      return;
    }
    setHistorySessions(data.sessions);
    setHistoryCursor(data.nextCursor ?? null);
    setHistoryHasMore(data.hasMore === true || data.nextCursor != null);
    setHistoryError(null);
  }, [data]);

  /** Correct the handset clock against the server response carrying the deadlines. */
  const serverNow = tick + (data ? Date.parse(data.now) - data.receivedAt : 0);

  async function act(
    what: () => Promise<unknown>,
    failure: string,
  ): Promise<boolean> {
    setBusy(true);
    setProblem(null);
    let succeeded = false;
    try {
      await what();
      succeeded = true;
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : failure);
    } finally {
      // Reconcile even after a failed/uncertain write. If the server accepted a
      // switch but the response was lost, the next click must use the real
      // current focus rather than stale browser state.
      reload();
      setBusy(false);
    }
    return succeeded;
  }

  async function log(event: FormEvent) {
    event.preventDefault();
    if (instrument.blocked) { setProblem(instrument.problem); return; }
    setSaved(null);
    await act(async () => {
      await send("POST", base, {
        ...(instrument.value?{instrument:instrument.value}:{}),
        minutes: asMinutes(minutes, seconds),
        name,
        needsWork: manualNeedsWork,
      });
      setMinutes("");
      setSeconds("");
      setName("");
      setManualNeedsWork(false);
      instrument.reset();
      setSaved("Logged.");
    }, "Could not log that.");
  }

  /** Start the overall timer or switch its current focus without stopping it. */
  async function chooseFocus(target: {
    practiceTodoId: string | null;
    name?: string | null;
  }) {
    setSaved(null);
    const switching = data?.timer != null;
    if (!switching && instrument.blocked) { setProblem(instrument.problem); return; }
    const succeeded = await act(
      async () => {
        if (switching) {
          await send("POST", `${base}/timer/focus`, {
            ...target,
            notes: focusNotes.trim() === "" ? null : focusNotes,
            needsWork: focusNeedsWork,
          });
        } else {
          await send("POST", `${base}/timer`, {...target,...(instrument.value?{instrument:instrument.value}:{})});
        }
      },
      switching
        ? "Could not switch practice focus."
        : "Could not start practice.",
    );

    if (switching) {
      // A failed mutation may still have reached the server. Clear reflection
      // state and reload above rather than letting an uncertain old-focus flag
      // leak onto whichever focus is current after reconciliation.
      setFocusNotes("");
      setFocusNeedsWork(false);
      if (succeeded) {
        // The overall practice row does not exist until Finish, but the focus
        // just left is already durable and its task total should refresh now.
        setFocusRevision((revision) => revision + 1);
      }
    }
  }

  useEffect(() => {
    const title = searchParams.get("log_title");
    if (!title || !data || handledLogTitle.current === title) return;
    handledLogTitle.current = title;
    setName((current) => current.trim() ? current : title.trim().slice(0, 200) || "Practice session");
    if (manualLogRef.current) manualLogRef.current.open = true;
    window.requestAnimationFrame(() => manualLogRef.current?.scrollIntoView({ block: "center", behavior: "smooth" }));
    const next = new URLSearchParams(searchParams);
    next.delete("log_title");
    setSearchParams(next, { replace: true });
  }, [data, searchParams, setSearchParams]);

  async function stop() {
    setSaved(null);
    const timer = data?.timer ?? null;
    await act(async () => {
      const result = await send<{ discarded: boolean }>(
        "POST",
        `${base}/timer/stop`,
        {
          // Used only by a pre-deploy timer that is still running without focus.
          name: timer?.focusName == null ? name : null,
          notes: focusNotes.trim() === "" ? null : focusNotes,
          needsWork:
            timer?.focusName == null ? manualNeedsWork : focusNeedsWork,
        },
      );
      setName("");
      setManualNeedsWork(false);
      setFocusNotes("");
      setFocusNeedsWork(false);
      instrument.reset();
      setSaved(
        result.discarded
          ? "That was too short to log."
          : "Practice finished and logged.",
      );
    }, "Could not finish practice.");
  }

  async function saveEdit() {
    if (!editing) return;
    setSaved(null);
    await act(async () => {
      await saveManualPracticeEntry(studentId, editing);
      setEditing(null);
    }, "Could not change that.");
  }

  const timer = data?.timer ?? null;
  const overdue = timer !== null && serverNow >= Date.parse(timer.givesUpAt);
  useEffect(() => {
    if (overdue) reload();
  }, [overdue, reload]);

  if (!data && error) return <div className="error" role="alert">
    Practice could not be loaded. {error} <button type="button" className="quiet" onClick={reload}>Retry practice</button>
  </div>;
  if (!data) return <p className="empty" role="status">Loading practice…</p>;

  const elapsed = timer ? (serverNow - Date.parse(timer.startedAt)) / 1000 : 0;
  const focusElapsed = timer?.focusStartedAt
    ? (serverNow - Date.parse(timer.focusStartedAt)) / 1000
    : elapsed;
  const asking =
    timer !== null && !overdue && serverNow >= Date.parse(timer.promptAt);
  const targetReached =
    timer?.suggestedMinutes != null &&
    focusElapsed >= timer.suggestedMinutes * 60;
  const freeActive =
    timer !== null && timer.practiceTodoId === null && timer.focusName !== null;
  const requestedFreeName = freePracticeName.trim() || "Free practice";
  const sameFreeFocus =
    freeActive &&
    (freePracticeName.trim() === "" || requestedFreeName === timer?.focusName);
  const canDiscardTimer =
    timer !== null && (timer.focusName == null || elapsed < MIN_TIMER_SECONDS);

  const historyRevision = historySessions
    .map(
      (session) =>
        `${session.id}:${session.durationSeconds}:${session.focusSegments
          .map((segment) => `${segment.id}:${segment.durationSeconds}`)
          .join(",")}`,
    )
    .join("|");
  const visibleSessions = selectedDay
    ? historySessions.filter(
        (session) => dayKey(session.startedAt) === selectedDay.date,
      )
    : historySessions;

  function selectHistoryDay(day: PracticeHistoryDay) {
    setSelectedDay((current) => (current?.date === day.date ? null : day));
    setTimeout(() => {
      document
        .getElementById("practice-session-history")
        ?.scrollIntoView?.({ block: "start" });
    }, 0);
  }

  async function loadOlderHistory() {
    if (historyLoading || historyCursor === null) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const next = normalisePracticeLog(
        await get<RawFocusPracticeLog>(base, {
          cursor: historyCursor,
          limit: "10",
        }),
      );
      setHistorySessions((current) => {
        const known = new Set(current.map((session) => session.id));
        return [...current, ...next.sessions.filter((session) => !known.has(session.id))];
      });
      setHistoryCursor(next.nextCursor ?? null);
      setHistoryHasMore(next.hasMore === true || next.nextCursor != null);
    } catch (cause: unknown) {
      setHistoryError(
        cause instanceof Error ? cause.message : "Could not load older practice.",
      );
    } finally {
      setHistoryLoading(false);
    }
  }

  function manualSessionContent(session: FocusPracticeSession) {
    if (editing?.id === session.id) {
      return <ManualPracticeEditor draft={editing} onChange={setEditing} busy={busy} onSave={() => saveEdit()} onCancel={() => setEditing(null)} />;
    }

    return (
      <div className="row" style={{ alignItems: "baseline", flexWrap: "wrap" }}>
        <strong className="tabular">
          {formatDuration(session.durationSeconds)}
        </strong>
        <span style={{ flex: "1 1 180px" }}>
          {session.name ?? (
            <span className="muted small">
              The timer stopped this one — say what it was
            </span>
          )}
          {session.needsWork && <span className="tag warn"> struggled</span>}
          {session.autoStopped && (
            <span
              className="small muted"
              title="Nobody answered when the timer asked"
            >
              {" "}
              · stopped automatically
            </span>
          )}
        </span>
        <button
          type="button"
          className="link small"
          onClick={() =>
            setEditing({
              id: session.id,
              minutes: String(Math.floor(session.durationSeconds / 60)),
              seconds: String(session.durationSeconds % 60),
              name: session.name ?? "",
            })
          }
        >
          edit
        </button>
      </div>
    );
  }

  return (
    <div className="family-practice-workspace">
      <h1>Practice</h1>
      <a className="small" href="/help/record-your-practice" target="_blank" rel="noreferrer">Help with practice</a>
      <PitchPracticeLink key={studentId} studentId={studentId}/>
      {timer ? <p className="small muted">Free practice instrument: <strong>{timer.instrument ? timer.instrument[0]!.toUpperCase()+timer.instrument.slice(1) : 'Not recorded'}</strong></p> : <PracticeInstrumentPicker choice={instrument} disabled={busy} />}

      <section aria-labelledby="practice-timer-heading" className="practice-timer-section">
        {error && <div className="error small" role="alert">{error}</div>}
        {problem && <div className="error small" role="alert">{problem}</div>}
        {saved && <ActionConfirmation variant={saved === "That was too short to log." ? "attention" : "success"} title={saved === "That was too short to log." ? "Practice timer ended" : "Practice recorded"} description={saved} />}
        <div className={`practice-timer-strip${timer ? " is-running" : ""}`}>
          <strong className="practice-timer-clock tabular">{clock(timer ? elapsed : 0)}</strong>
          <div className="practice-timer-copy">
            <h2 id="practice-timer-heading" className="small muted">{timer ? "Total practice" : "Practice timer"}</h2>
            <p>{timer ? timer.focusName ?? "Practice in progress" : "Start a task below, or choose free practice."}</p>
            {timer?.focusName && <span className="small muted">
              This focus: {clock(focusElapsed)}{timer.suggestedMinutes != null && ` · Aim for ${timer.suggestedMinutes} min/day`}
            </span>}
          </div>
          <div className="practice-timer-actions">
            <button type="button" className="quiet small" aria-expanded={freePracticeOpen}
              aria-controls="practice-free-form" disabled={busy || !data}
              onClick={() => setFreePracticeOpen((open) => !open)}>Free practice</button>
            {timer && <button type="button" disabled={busy || (timer.focusName == null && name.trim() === "")}
              onClick={() => void stop()}>{busy ? "Saving…" : "Finish practice"}</button>}
          </div>
        </div>
        {timer && targetReached && <p className="small practice-target-status" role="status">
          Suggested target reached. Keep going, switch focus below, or finish when you are ready.
        </p>}
        {timer && asking && <div className="notice" role="alert">
          <strong>Still practising?</strong>
          <p className="small muted">Your timer keeps running with the screen off. Confirm when you return to schedule the next check. If you never return, it closes at the eight-hour limit and only credits practice up to this check-in point.</p>
          <button type="button" disabled={busy} onClick={() => void act(() => send("POST", `${base}/timer/confirm`), "Could not carry on.")}>Yes, keep going</button>
        </div>}
        {freePracticeOpen && <div id="practice-free-form" className="practice-free-form">
          <label htmlFor="free-practice-title" className="small muted">What would you like to work on? (optional)</label>
          <div className="row">
            <input id="free-practice-title" type="text" value={freePracticeName} maxLength={200}
              onChange={(event) => setFreePracticeName(event.target.value)} placeholder="e.g. improvisation or a new song" />
            <button type="button" disabled={busy || sameFreeFocus || !data || (!timer && instrument.blocked)} aria-pressed={sameFreeFocus}
              onClick={() => void chooseFocus({ practiceTodoId: null, name: freePracticeName.trim() === "" ? null : freePracticeName })}>
              {sameFreeFocus ? "Practising now" : timer ? "Switch to free practice" : "Start free practice"}
            </button>
          </div>
        </div>}
        {timer?.focusName == null && timer && <label className="practice-legacy-name">
          <span className="small">What did you work on?</span>
          <input type="text" value={name} onChange={(event) => setName(event.target.value)} maxLength={200} required />
        </label>}
        {timer && <details className="practice-reflection">
          <summary>Add a note about this focus (optional)</summary>
          <label>
            <span className="visually-hidden">Note about this focus</span>
            <textarea value={focusNotes} onChange={(event) => setFocusNotes(event.target.value)} maxLength={2000} rows={2}
              placeholder="What improved, what was difficult, or what to remember next time" />
          </label>
        </details>}
        {timer && <div className="practice-timer-feedback row">
            <label className="row small"><input type="checkbox" checked={timer.focusName == null ? manualNeedsWork : focusNeedsWork}
              onChange={(event) => timer.focusName == null ? setManualNeedsWork(event.target.checked) : setFocusNeedsWork(event.target.checked)} />
              <span>Struggled with this</span>
            </label>
            {canDiscardTimer && <button type="button" className="quiet small" disabled={busy}
              onClick={() => void act(() => send("DELETE", `${base}/timer`), "Could not discard it.")}>Discard timer</button>}
        </div>}
      </section>

      {!timer && (
        <details ref={manualLogRef} className="practice-manual-log" onToggle={event => {
          if (event.currentTarget.open) setName(current => current.trim() ? current : "Practice session");
        }}>
          <summary>
            <strong>Log practice without the timer</strong>
          </summary>
          <form onSubmit={log} className="stack" style={{ marginTop: 12 }}>
            <div className="practice-duration-presets row">
              {COMMON_MINUTES.map((value) => (
                <button key={value} type="button" className={minutes === String(value) ? "small" : "quiet small"}
                  onClick={() => setMinutes(String(value))}>{value} min</button>
              ))}
            </div>
            <div className="practice-duration-row">
              <label className="practice-duration-field"><span className="small muted">Minutes</span>
                <input type="number" min={0} max={480} value={minutes} onChange={(event) => setMinutes(event.target.value)} placeholder="min" aria-label="Minutes" />
              </label>
              <label className="practice-duration-field"><span className="small muted">Seconds</span>
                <input type="number" min={0} max={59} value={seconds} onChange={(event) => setSeconds(event.target.value)} placeholder="sec" aria-label="Seconds" />
              </label>
            </div>
            <label className="stack"><span className="small muted">What did you practise?</span>
              <input type="text" value={name} onChange={(event) => setName(event.target.value)} maxLength={200} required style={{ width: "100%" }} />
            </label>
            <div className="row" style={{ flexWrap: "wrap" }}>
              <label className="row small"><input type="checkbox" checked={manualNeedsWork} onChange={(event) => setManualNeedsWork(event.target.checked)} />
                <span>Struggled with this</span>
              </label>
              <button type="submit" disabled={busy || instrument.blocked || asMinutes(minutes, seconds) <= 0 || name.trim() === ""}>
                {busy ? "Saving…" : "Log it"}
              </button>
            </div>
          </form>
        </details>
      )}

      <div className="practice-workspace-grid">
        <div className="practice-tasks">
          <StudentNeckDiagrams key={`neck:${studentId}`} studentId={studentId} family />
          <FamilyPracticeTodos
            key={`todos:${studentId}`}
            studentId={studentId}
            timerRunning={timer !== null}
            activeTodoId={timer?.practiceTodoId ?? null}
            practiceBusy={busy || !data || (!timer && instrument.blocked)}
            revision={`${historyRevision}:${focusRevision}`}
            toolLinks={toolLinks}
            learnerInstruments={instrument.loading || instrument.error ? undefined : instrument.instruments}
            onPractise={(todo) => chooseFocus({ practiceTodoId: todo.id })}
            onLogPractice={(todo) => {
              setName((current) => current.trim() ? current : todo.title.trim().slice(0, 200) || "Practice session");
              const details = manualLogRef.current;
              if (details) details.open = true;
              window.requestAnimationFrame(() => manualLogRef.current?.scrollIntoView({ block: "center", behavior: "smooth" }));
            }}
          />
        </div>
        <FamilyResources key={studentId} studentId={studentId} />
      </div>

      <PracticeHistory
        endpoint={`${base}/history`}
        variant="family"
        showCalendar
        collapsible
        revision={historyRevision}
        selectedDate={selectedDay?.date ?? null}
        onSelectDay={selectHistoryDay}
      >
      <section
        id="practice-session-history"
        style={{ scrollMarginTop: 16 }}
        aria-labelledby="recent-practice-heading"
      >
        <div
          className="spread"
          style={{ alignItems: "baseline", gap: 12, flexWrap: "wrap" }}
        >
          <h2 id="recent-practice-heading">
            {selectedDay
              ? `Practice on ${formatWeekdayDay(selectedDay.date)}`
              : "Recent practice"}
          </h2>
          {selectedDay && (
            <button
              type="button"
              className="link small"
              onClick={() => setSelectedDay(null)}
            >
              Show all recent practice
            </button>
          )}
        </div>

        {visibleSessions.length === 0 && (
          <div className="panel">
            <p className="empty">
              {selectedDay
                ? selectedDay.sessions === 0
                  ? "No practice was logged on this day."
                  : "This practice day is in the summary, but its detail is outside the recent-practice list."
                : "No practice logged yet."}
            </p>
          </div>
        )}

        {visibleSessions.map((session) => {
          const guided = session.focusSegments.length > 0;
          return (
            <div className="panel session" key={session.id}>
              <div
                className="spread"
                style={{ alignItems: "baseline", gap: 12, flexWrap: "wrap" }}
              >
                <span className="small muted tabular">
                  {formatDate(session.startedAt)}
                </span>
                {guided && (
                  <strong className="tabular">
                    {formatDuration(session.durationSeconds)}
                  </strong>
                )}
              </div>

              {guided ? (
                <>
                  <div
                    className="row"
                    style={{
                      alignItems: "baseline",
                      flexWrap: "wrap",
                      marginTop: 6,
                    }}
                  >
                    <strong style={{ flex: "1 1 180px" }}>
                      {session.name ?? "Practice session"}
                    </strong>
                    {session.needsWork && (
                      <span className="tag warn">struggled</span>
                    )}
                    {session.autoStopped && (
                      <span className="small muted">stopped automatically</span>
                    )}
                  </div>
                  <div className="stack" style={{ gap: 8, marginTop: 10 }}>
                    {session.focusSegments.map((segment) => (
                      <div className="notice small" key={segment.id}>
                        <div
                          className="row"
                          style={{ alignItems: "baseline", flexWrap: "wrap" }}
                        >
                          <strong className="tabular">
                            {formatDuration(segment.durationSeconds)}
                          </strong>
                          <span style={{ flex: "1 1 180px" }}>
                            {segment.focusName}
                          </span>
                          {segment.needsWork && (
                            <span className="tag warn">struggled</span>
                          )}
                        </div>
                        {segment.notes && (
                          <p
                            className="small muted"
                            style={{ margin: "6px 0 0" }}
                          >
                            {segment.notes}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{ marginTop: 6 }}>
                  {manualSessionContent(session)}
                  {session.notes && (
                    <p className="small muted" style={{ margin: "6px 0 0" }}>
                      {session.notes}
                    </p>
                  )}
                </div>
              )}

              <PracticeAttachments
                base={`${base}/${session.id}/attachments`}
                attachments={session.attachments}
                taskOptions={session.attachmentTasks ?? []}
                recordingsEnabled={session.recordingsEnabled ?? false}
                canEdit
                onChanged={reload}
              />
            </div>
          );
        })}

        {historyHasMore && (
          <div className="row" style={{ alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              className="action-tertiary small"
              disabled={historyLoading || historyCursor === null}
              onClick={() => void loadOlderHistory()}
            >
              {historyLoading ? "Loading older…" : "Load older"}
            </button>
            {historyError && <span className="error small">{historyError}</span>}
          </div>
        )}
      </section>
      </PracticeHistory>
    </div>
  );
}
